// utils/bundles.js

const asArray = (v) => Array.isArray(v) ? v : (v ? [v] : []);
const uniq = (arr) => Array.from(new Set(arr.filter(Boolean)));
const take = (arr, n) => (Array.isArray(arr) ? arr.slice(0, n) : []);

/* *******************************************************************
 * --- Core Tag/Bucket Helpers ---
 ******************************************************************* */

const titleCase = (s = '') =>
  s.replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b([a-z])/g, m => m.toUpperCase());

const toSetLower = (arr = []) => {
  const out = new Set();
  (arr || []).forEach(v => {
    if (typeof v === 'string' && v.trim()) out.add(v.trim().toLowerCase());
  });
  return out;
};

const intersectManyLower = (sets) => {
  if (!sets || !sets.length) return new Set();
  const [first, ...rest] = sets;
  const out = new Set(first);
  for (const s of rest) {
    for (const v of Array.from(out)) {
      if (!s.has(v)) out.delete(v);
    }
  }
  return out;
};

export const prettyGrade = (gRaw = '') => {
  const g = gRaw.trim().toLowerCase();
  if (!g) return '';
  if (/^grade\s*\d+$/.test(g)) return titleCase(g);
  if (/^\d{1,2}$/.test(g)) return `Grade ${g}`;
  if (g === 'k' || g === 'kindergarten') return 'Kindergarten';
  if (g === 'pre-k' || g === 'prek' || g === 'pre k' || g === 'prekindergarten') return 'Pre-K';
  const yr = g.match(/^year\s*(\d{1,2})$/);
  if (yr) return `Year ${yr[1]}`;
  if (g === 'ks3' || g === 'key stage 3') return 'KS3';
  if (g === 'ks4' || g === 'key stage 4') return 'KS4';
  return titleCase(g);
};

const SEASON_KEYWORDS = [
  'back to school', 'beginning of year', 'end of year', 'first week',
  'fall', 'autumn', 'halloween', 'thanksgiving', 'bonfire night',
  'winter', 'christmas', 'new year', 'valentine', "valentine's",
  'spring', 'easter', 'ramadan', 'eid', 'hanukkah', 'diwali',
  'summer', 'earth day', 'black history month', "women's history month",
  'pride', 'st patrick', 'st. patrick', "mother's day", 'fathers day', "father's day"
];
const isSeasonal = (t = '') => SEASON_KEYWORDS.some(k => t.toLowerCase().includes(k));

export const getBuckets = (p = {}) => {
  const subjects1 = p.subjectTags || [];
  const grades1   = p.gradeTags || [];
  const seasonal1 = p.seasonalTags || [];
  const types1    = p.resourceTypeTags || [];
  const subjects2 = [...(p._subjectAreaTags || []), ...(p._specificSubjectTags || [])];
  const grades2   = p._gradeRefs || [];
  const seasonal2 = p._seasonalRefs || [];
  const types2    = p._typeRefs || [];
  const gradesScr    = p.scrapedGrades || [];
  const subjectsScr  = p.scrapedSubjects || [];
  const typesScr     = p.scrapedTypes || [];
  const seasonsScr   = p.scrapedSeasonal || p.scrapedSeasons || [];
  const coll = p.tagsCollated || {};
  const subjects3 = coll.subjects || [];
  const grades3   = coll.grades || [];
  const seasonal3 = coll.seasonal || [];
  const types3    = coll.types || [];
  const singleSubject = [];
  if (typeof p.subject === 'string') singleSubject.push(p.subject);
  if (typeof p.primarySubject === 'string') singleSubject.push(p.primarySubject);
  const subjectsRaw = toSetLower([...subjects1, ...subjects2, ...subjects3, ...subjectsScr, ...singleSubject]);
  const gradesRaw   = toSetLower([...grades1,   ...grades2,   ...grades3,   ...gradesScr]);
  const seasonalRaw = toSetLower([...seasonal1, ...seasonal2, ...seasonal3, ...seasonsScr]);
  const typesRawAll = toSetLower([...types1,    ...types2,    ...types3,    ...typesScr]);
  const seasonalFromTypes = new Set(Array.from(typesRawAll).filter(isSeasonal));
  const typesClean        = new Set(Array.from(typesRawAll).filter(t => !isSeasonal(t)));
  const seasonalUnion     = new Set([...seasonalRaw, ...seasonalFromTypes]);
  return { subjects: subjectsRaw, grades: gradesRaw, seasonal: seasonalUnion, types: typesClean };
};

/* *******************************************************************
 * --- Upgraded buildWhyItWorks ---
 ******************************************************************* */
export const buildWhyItWorks = (s) => {
  const allBuckets = (s.products || []).map(p => p.__buckets || getBuckets(p));
  const allSubjects = new Set();
  const allGrades = new Set();
  const allSeasonal = new Set();
  const allTypes = new Set();

  allBuckets.forEach(b => {
    b.subjects.forEach(t => allSubjects.add(titleCase(t)));
    b.grades.forEach(t => allGrades.add(prettyGrade(t)));
    b.seasonal.forEach(t => allSeasonal.add(titleCase(t)));
    b.types.forEach(t => allTypes.add(titleCase(t)));
  });

  const shared = s.matchTags;
  if (shared.subjects.length === 0 && shared.grades.length === 0 && shared.seasonal.length === 0 && shared.types.length === 0) {
    return {
      noMatch: 'These products cover complementary topics but do not share common tags.',
      shared: {},
      all: {}
    };
  }

  return {
    shared: {
      subjects: shared.subjects,
      grades: shared.grades,
      seasonal: shared.seasonal,
      types: shared.types,
    },
    all: {
      subjects: Array.from(allSubjects),
      grades: Array.from(allGrades),
      seasonal: Array.from(allSeasonal),
      types: Array.from(allTypes),
    }
  };
};

/* *******************************************************************
 * --- Bundle Proposal Logic (Unchanged) ---
 ******************************************************************* */

export function normalizeProduct(p) {
  const buckets = getBuckets(p);
  const title = p.productTitle || p.title || '';
  const url = p.tptUrl || p.url || '';
  const id = p.id || p.docId || p.slug || title || url || Math.random().toString(36).slice(2);
  return {
    id, productTitle: title, tptUrl: url,
    tags: buckets.subjects, kw: buckets.types, standards: buckets.grades,
  };
}

function stripHtml(html = '') { return html.replace(/<[^>]+>/g, ' '); }
function overlapScore(a, b) {
  const A = new Set(a); const B = new Set(b);
  let inter = 0; for (const x of A) if (B.has(x)) inter++; return inter;
}
function productAffinity(pa, pb) {
  const tag = overlapScore(pa.tags, pb.tags);
  const kw = overlapScore(pa.kw, pb.kw);
  const std = overlapScore(pa.standards, pb.standards);
  return tag * 3 + std * 2 + kw;
}
function scoreGroup(list) {
  let score = 0;
  for (let i=0;i<list.length;i++) for (let j=i+1;j<list.length;j++) score += productAffinity(list[i], list[j]);
  return score;
}
function levelFromScore(score, size) {
  const norm = score / Math.max(1, (size*(size-1))/2);
  if (norm >= 6) return 'strong';
  if (norm >= 3) return 'good';
  return 'potential';
}
function* combinations(arr, minSize=2, maxSize=4) {
  const n = arr.length;
  function* choose(start, path) {
    if (path.length >= minSize && path.length <= maxSize) yield path.slice();
    if (path.length === maxSize) return;
    for (let i=start;i<n;i++){ path.push(arr[i]); yield* choose(i+1, path); path.pop(); }
  }
  yield* choose(0, []);
}
export function proposeBundles(products, { minSize=2, maxSize=4 } = {}) {
  const pool = products.map(normalizeProduct);
  const base = pool.slice(0, Math.min(pool.length, 30));
  const out = [];
  let collected = 0;
  for (const ids of combinations(base, minSize, maxSize)) {
    const score = scoreGroup(ids);
    const level = levelFromScore(score, ids.length);
    const sharedTags = intersectMany(ids.map(x => x.tags));
    out.push({
      products: ids.map(x => mapBack(x, products)),
      tags: take(sharedTags, 20),
      score,
      level
    });
    if (++collected > 400) break;
  }
  out.sort((a,b) => (b.score - a.score) || (b.products.length - a.products.length));
  const seen = new Set(), uniqOut = [];
  for (const c of out) {
    const key = c.products.map(p => p.id).sort().join('|');
    if (!seen.has(key)) { seen.add(key); uniqOut.push(c); }
    if (uniqOut.length >= 80) break;
  }
  return uniqOut;
}
function intersectMany(arrs) {
  if (!arrs.length) return [];
  let S = new Set(arrs[0]);
  for (let i=1;i<arrs.length;i++){ const next = new Set(arrs[i]); S = new Set([...S].filter(x => next.has(x))); }
  return Array.from(S);
}
function mapBack(np, originalList) {
  return originalList.find(p => p.id === np.id) || { id: np.id, productTitle: np.productTitle, tptUrl: np.tptUrl };
}
export function createBundleTitle(bundle, { subject='', gradeBand='' } = {}) {
  const parts = []; if (subject) parts.push(subject); if (gradeBand) parts.push(gradeBand);
  const topic = topTag(bundle); if (topic) parts.push(cap(topic));
  const size = bundle.products?.length || 0;
  const rt = inferResourceTypes(bundle.products || []);
  const tail = rt ? `${rt} Bundle` : 'Bundle';
  const head = parts.length ? parts.join(' ') : 'Classroom';
  return `${head} ${tail}${size ? ` (${size} Resources)` : ''}`.replace(/\s+/g,' ').trim();
}
function topTag(bundle){ const c={}; (bundle.tags||[]).forEach(t=>{c[t]=(c[t]||0)+1}); let top=null,best=0; for(const [k,v] of Object.entries(c)) if(v>best){best=v; top=k} return top; }
function cap(s=''){ return s.charAt(0).toUpperCase()+s.slice(1); }
function inferResourceTypes(ps){ const rt=new Set(); (ps||[]).forEach(p=>{ asArray(p.resourceTypeTags).forEach(t=>rt.add(t)); }); const arr=Array.from(rt); if(!arr.length) return ''; return arr.map(cap).slice(0,2).join(' + ') || ''; }

/* *******************************************************************
 * --- Upgraded Prompt Builders ---
 ******************************************************************* */

const analyzeBundleProducts = (products) => {
  const allBuckets = products.map(p => p.__buckets || getBuckets(p));
  const allSubjects = new Set();
  const allGrades = new Set();
  const allTypes = new Set();
  const allSeasons = new Set();
  const allTitles = [];
  const allDescriptions = [];

  products.forEach(p => {
    allTitles.push(p.productTitle || p.title || '');
    allDescriptions.push(p.descriptionText || p.description || '');
  });

  allBuckets.forEach(b => {
    b.subjects.forEach(t => allSubjects.add(titleCase(t)));
    b.grades.forEach(t => allGrades.add(prettyGrade(t)));
    b.types.forEach(t => allTypes.add(titleCase(t)));
    b.seasonal.forEach(t => allSeasons.add(titleCase(t)));
  });

  return {
    subjects: Array.from(allSubjects),
    grades: Array.from(allGrades),
    resourceTypes: Array.from(allTypes),
    seasons: Array.from(allSeasons),
    titles: allTitles,
    descriptions: allDescriptions
  };
};

export function buildBundlePrompts({
  products, title, tone='Clear & Professional', audience='teachers',
  subject='', gradeBand='', descriptionLength='medium', includeUrls=true,
  relatedProducts = [],
}) {
  const analysis = analyzeBundleProducts(products);

  const sizeGuide =
    descriptionLength === 'short' ? '1-2 paragraphs' :
    descriptionLength === 'long'  ? '4-5 paragraphs' : '2-3 paragraphs';

  const systemPrompt = `You are an expert TPT copywriter and SEO specialist. Your goal is to create a compelling, easy-to-read, and highly optimized product description for a TPT product bundle.
Your tone should be **${tone}**.
The final output must be well-structured HTML. Use <h3> for main headings with emojis, <strong> for emphasis, and <ul> with <li> for lists.
For all paragraphs (like the Hook and Introduction), you MUST use multiple <p> tags to break up thoughts. Each <p> tag should contain only 2-3 sentences maximum to prevent long "walls of text".`;

  const userPrompt = `
Here is all the data I have on the products in this bundle. Please synthesize it into one master description.

**BUNDLE DATA:**
- **Proposed Title:** "${title}"
- **Total Products:** ${products.length}
- **Target Audience:** ${audience}
- **Desired Length:** ${sizeGuide}

**AGGREGATED TAGS (from all products):**
- **Grades Covered:** ${analysis.grades.join(', ') || 'Not specified'}
- **Subjects Covered:** ${analysis.subjects.join(', ') || 'Not specified'}
- **Resource Types Included:** ${analysis.resourceTypes.join(', ') || 'Not specified'}
- **Seasonal Themes:** ${analysis.seasons.join(', ') || 'None'}

**INDIVIDUAL PRODUCT TITLES (for theme analysis):**
${analysis.titles.map(t => `- ${t}`).join('\n')}

**INDIVIDUAL PRODUCT DESCRIPTIONS (for theme analysis):**
${analysis.descriptions.map((d, i) => `--- Product ${i+1} Description ---\n${stripHtml(d).slice(0, 200)}...\n`).join('\n')}

---
**YOUR TASK: Write the Bundle Description following this exact template:**

<p>[HOOK PARAGRAPH: Write a 1-2 sentence compelling hook that addresses a common problem for a ${audience} teaching ${analysis.subjects[0] || 'this topic'}.]</p>

<p>[INTRODUCTION PARAGRAPH: Write a passionate paragraph that introduces the bundle as the perfect solution. Mention the core theme you found from the product titles (e.g., "fraction mastery," "end of year review").]</p>

<h3>✨ What's Included...</h3>
<ul>
${products.map((p, i) => {
  const tt = p.productTitle || p.title || `Item ${i+1}`;
  const url = p.tptUrl || '#';
  return `  <li>${includeUrls && url ? `<a href="${url}">${tt}</a>` : tt}</li>`;
}).join('\n')}
</ul>

<h3>❤️ Why You'll Love This Resource!</h3>
<ul>
  <li>[BENEFIT 1: Synthesize a key benefit from the product descriptions, e.g., "<strong>Saves you hours of prep time</strong>..."]</li>
  <li>[BENEFIT 2: Synthesize another key benefit, e.g., "<strong>100% editable</strong> and easy to differentiate..."]</li>
  <li>[BENEFIT 3: Synthesize another key benefit, e.g., "Keeps students <strong>engaged</strong> and skills sharp..."]</li>
  <li>[BENEFIT 4: Synthesize another key benefit, e.g., "Perfect for <strong>progress monitoring</strong>..."]</li>
</ul>

<h3>✎ How it works...</h3>
<ul>
  <li>[USAGE 1: Describe a way to use it, e.g., "Perfect for <strong>math homework</strong>, <strong>morning work</strong>, or <strong>math centers</strong>."]</li>
  <li>[USAGE 2: Describe another use, e.g., "Use as <strong>daily warm-ups</strong>..."]</li>
  <li>[USAGE 3: Describe another use, e.g., "Great for <strong>review</strong> before standardized testing."]</li>
</ul>

${relatedProducts.length > 0 ? `
<h3>➡️ You Might Also Like...</h3>
<ul>
${relatedProducts.map(p => `  <li><a href="${p.tptUrl || '#'}">${p.productTitle || p.title}</a></li>`).join('\n')}
</ul>
` : ''}

<p>[FINAL CLOSING PARAGRAPH & CALL TO ACTION: Write a final, enthusiastic 1-2 sentence paragraph, followed by a strong, direct call to action like "Click <strong>Add to Cart</strong>..."]</p>
`.trim();

  return { systemPrompt, userPrompt };
}

/**
 * NEW: Brief Analysis Prompt
 */
export const buildDescriptionAnalysisPrompt = (description, title, productList) => {
  return `
You are an expert TPT SEO Auditor. Analyze the following TPT product description.
Provide your analysis *only* as a minified JSON object with the keys: "seoStrength", "seoImprovement", "readability", "persuasiveness".
Keep each value to a single, brief sentence.

**Title:**
${title}

**Product List:**
${productList.join(', ')}

**Description to Analyze:**
${stripHtml(description)}

JSON response only.
  `.trim();
};

/**
 * NEW: Upgraded Title Prompt (with Title Case)
 */
export const buildBundleTitlePrompt = (selectedProducts) => {
  const analysis = analyzeBundleProducts(selectedProducts);

  return `
You are an expert TPT (Teachers Pay Teachers) SEO copywriter. Your goal is to generate 5 creative, compelling, and SEO-driven titles for a TPT product bundle.

DATA:
- **Product Titles in Bundle:** ${analysis.titles.join('; ')}
- **Main Subject(s):** ${analysis.subjects.join(', ') || 'N/A'}
- **Main Grade(s):** ${analysis.grades.join(', ') || 'N/A'}
- **Resource Types:** ${analysis.resourceTypes.join(', ') || 'N/A'}
- **Total Products:** ${analysis.titles.length}

RULES:
1.  **Analyze Themes:** Use the 'Product Titles in Bundle' list to find the core *theme* (e.g., "Fraction Mastery," "End of Year," "Classroom Management").
2.  **Strict Character Limit:** Each title MUST be **87 characters or less**.
3.  **Keyword Foundation:** Titles MUST include the Main Subject and Main Grade (if provided).
4.  **Bundle Wording:** Titles MUST use words like "Bundle," "Mega Bundle," "Growing Bundle," or "Activity Pack."
5.  **Creative Angles:**
    * **Keyword-Driven:** e.g., "${analysis.grades[0] || 'Math'} ${analysis.subjects[0] || 'Activities'} Bundle | ${analysis.resourceTypes[0] || 'Worksheets'}"
    * **Benefit-Driven:** e.g., "The Complete ${analysis.grades[0] || 'Math'} ${analysis.subjects[0] || 'Unit'} | No-Prep Bundle"
    * **Feature-Packed:** e.g., "Engaging ${analysis.subjects[0] || 'Classroom'} Bundle | Digital & Print"
6.  **Format:** Use **Title Case** (Capitalize Major Words). Use pipes (|) or commas (,) to separate concepts.
7.  **Do NOT** use grade ranges (e.g., "3-5"). Stick to the Main Grade.

OUTPUT FORMAT (minified JSON):
{"titles":["Title 1", "Title 2", "Title 3", "Title 4", "Title 5"]}
  `.trim();
};

/**
 * NEW: Prompt to refine an existing description
 */
export const buildRefinePrompt = ({ currentTitle, currentDescription, rephraseRequest }) => {
  const systemPrompt = `You are an expert TPT copywriter. Your task is to edit and refine an existing HTML description based on a user's request.
You MUST maintain the exact HTML structure (p, h3, ul, li, strong, a).
Only change the text content to meet the user's request.
Return *only* the new, full HTML description.`;

  const userPrompt = `
**USER REQUEST:**
"${rephraseRequest}"

**CURRENT BUNDLE TITLE:**
"${currentTitle}"

**CURRENT HTML DESCRIPTION (to refine):**
${currentDescription}

**YOUR TASK:**
Rewrite the full HTML description above to incorporate the user's request.
Remember to maintain the HTML structure.
`.trim();

  return { systemPrompt, userPrompt };
};


export function formatExportForTPT({ title, body }) {
  const safeTitle = (title || '').trim() || 'Bundle Title';
  const safeBody = (body || '').trim() || '(Paste your bundle description here.)';
  return [
    `${safeTitle}`,
    ``,
    `— Suggested Listing Block —`,
    `Price: $[set your price]  •  You Save: [estimate % or $]`,
    `License: [Single / Multiple]`,
    ``,
    safeBody,
    ``,
    `Notes:`,
    `• Replace placeholders above (price, save %) before publishing.`,
    `• Consider adding a short usage note (e.g., printable + Google Slides).`,
  ].join('\n');
}